import { useState } from "react";
import { ArrowLeft, KeyRound, Loader2 } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import Overview from "@/components/admin/Overview";
import Inquiries from "@/components/admin/Inquiries";
import Visitors from "@/components/admin/Visitors";

const TABS = [
  ["overview", "OVERVIEW"],
  ["inquiries", "INQUIRIES"],
  ["visitors", "VISITORS"],
];

function KeyGate({ onUnlock }) {
  const [key, setKey] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setState("loading");
    setError("");
    try {
      await adminApi.summary(key);
      onUnlock(key);
    } catch (err) {
      setError(err.message);
      setState("error");
    }
  }

  return (
    <form onSubmit={submit} data-testid="visits-key-form" className="mt-10 flex max-w-md items-end gap-3">
      <div className="flex-1">
        <label htmlFor="admin-key" className="font-mono text-[10px] tracking-[0.25em] text-fog">
          ADMIN KEY
        </label>
        <input
          id="admin-key"
          data-testid="admin-key-input"
          type="password"
          autoComplete="off"
          required
          value={key}
          onChange={(e) => setKey(e.target.value)}
          className="w-full border-b border-white/20 bg-transparent py-3 font-mono text-sm text-white outline-none transition-colors focus:border-accent"
        />
      </div>
      <button
        type="submit"
        data-testid="admin-load-button"
        disabled={state === "loading"}
        className="flex items-center gap-2 bg-white px-6 py-3 font-mono text-[11px] tracking-[0.2em] text-ink transition-colors hover:bg-accent disabled:opacity-70"
      >
        {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
        UNLOCK
      </button>
      {error && (
        <p role="alert" data-testid="visits-error" className="font-mono text-xs text-red-400">
          {error}
        </p>
      )}
    </form>
  );
}

export default function VisitsAdmin() {
  const [adminKey, setAdminKey] = useState("");
  const [tab, setTab] = useState("overview");

  return (
    <div data-testid="visits-page" className="min-h-screen bg-ink px-6 py-24 lg:px-10">
      <div className="mx-auto max-w-6xl">
        <a
          data-testid="visits-back-link"
          href="/"
          className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-fog transition-colors hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> BACK TO SITE
        </a>

        <h1 className="mt-8 font-display text-3xl font-black tracking-tight text-white sm:text-4xl">Site admin</h1>
        <p className="mt-2 font-mono text-[11px] tracking-[0.2em] text-fog">
          OWNER ONLY — VISITORS ARE ANONYMOUS UNTIL THEY SEND A MESSAGE
        </p>

        {!adminKey ? (
          <KeyGate onUnlock={setAdminKey} />
        ) : (
          <>
            <div data-testid="admin-tabs" className="mt-10 flex flex-wrap gap-2 border-b border-white/10 pb-4">
              {TABS.map(([id, label]) => (
                <button
                  key={id}
                  data-testid={`admin-tab-${id}`}
                  onClick={() => setTab(id)}
                  className={`px-5 py-2 font-mono text-[11px] tracking-[0.2em] transition-colors ${
                    tab === id ? "bg-white text-ink" : "border border-white/20 text-fog hover:border-white hover:text-white"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {tab === "overview" && <Overview adminKey={adminKey} />}
            {tab === "inquiries" && <Inquiries adminKey={adminKey} />}
            {tab === "visitors" && <Visitors adminKey={adminKey} />}
          </>
        )}
      </div>
    </div>
  );
}
